import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { FlaskConical, Clock, CheckCircle2, AlertOctagon, Microscope } from 'lucide-react';
import { dashboardApi } from '@/lib/api';
import type { LabDashboardData } from './types';
import {
  MetricCard, MetricsGrid, SectionCard, UrgencyBadge,
  DashboardShell, ScrollTable, Th, Td, TATBar, LoadingState, ErrorState,
} from './_shared';

// ── Component ───────────────────────────────────────────────────────────────
export default function LabTechnicianDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<LabDashboardData>({
    queryKey:        ['dashboard-lab'],
    queryFn:         () => dashboardApi.lab().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;


  return (
    <DashboardShell
      title="Lab Technician Dashboard"
      subtitle="Sample queue & critical value alerts · live view"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      {/* KPI metrics */}
      <MetricsGrid>
        <MetricCard icon={Microscope}    label="Total Samples Today"  value={data.totalSamples}      accent="bg-orange/15"/>
        <MetricCard icon={FlaskConical}  label="Pending Processing"   value={data.pendingProcessing}  accent="bg-blue-50"/>
        <MetricCard icon={Clock}         label="TAT Breached"         value={data.delayedTat}          accent="bg-red-50"  alert={data.delayedTat > 0}/>
        <MetricCard icon={CheckCircle2}  label="Verified Today"       value={data.verifiedToday}       accent="bg-emerald-50"/>
      </MetricsGrid>

      {/* Critical Values */}
      {data.criticalValues.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-red-200 bg-red-100/60">
            <AlertOctagon size={15} className="text-red-600 shrink-0"/>
            <span className="text-sm font-semibold text-red-700">Critical Values — Immediate Action Required</span>
            <span className="ml-auto bg-red-600 text-white text-2xs font-bold px-2 py-0.5 rounded-full">
              {data.criticalValues.length}
            </span>
          </div>
          <ScrollTable>
            <thead>
              <tr>
                <Th>Sample</Th><Th>Patient</Th><Th>Test</Th><Th>Parameter</Th>
                <Th right>Value</Th><Th>Normal Range</Th><Th>Flagged At</Th>
              </tr>
            </thead>
            <tbody>
              {data.criticalValues.map(cv => (
                <tr key={cv.sampleId + cv.parameter} className="bg-red-50/60">
                  <Td><span className="font-mono text-xs text-navy">{cv.sampleId}</span></Td>
                  <Td>{cv.patientName}</Td>
                  <Td>{cv.testName}</Td>
                  <Td><span className="font-semibold text-navy">{cv.parameter}</span></Td>
                  <Td right>
                    <span className="font-bold text-red-600">{cv.value} {cv.unit}</span>
                  </Td>
                  <Td muted>{cv.normalRange} {cv.unit}</Td>
                  <Td muted>{cv.criticalAt}</Td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </div>
      )}

      {/* Sample Queue */}
      <SectionCard title={`Sample Queue (${data.queue.length})`}>
        <ScrollTable>
          <thead>
            <tr>
              <Th>Sample ID</Th><Th>Patient</Th><Th>Test</Th>
              <Th>Priority</Th><Th>Collected</Th><Th>TAT Progress</Th>
            </tr>
          </thead>
          <tbody>
            {data.queue.map(s => (
              <tr key={s.sampleId} className={clsx(
                'hover:bg-gray-50 transition',
                s.elapsedMinutes >= s.tatMinutes && 'bg-red-50/40',
              )}>
                <Td><span className="font-mono text-xs text-navy">{s.sampleId}</span></Td>
                <Td><span className="font-medium">{s.patientName}</span></Td>
                <Td>{s.testName}</Td>
                <Td><UrgencyBadge urgency={s.urgency}/></Td>
                <Td muted>{s.collectedAt}</Td>
                <Td><TATBar elapsed={s.elapsedMinutes} tat={s.tatMinutes}/></Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </SectionCard>

    </DashboardShell>
  );
}
